
import { useDataQuery, useDataMutation } from '@dhis2/app-runtime';
import { CHECKIN_IDS, CHECKIN_STATUS, CHECKIN_PERIODS, getCheckInPeriodLabel } from '../utils/checkInConstants';


// Query to get position TEI for a staff member
const STAFF_POSITION_QUERY = {
  position: {
    resource: 'trackedEntityInstances',
    params: ({ staffId }) => ({
      trackedEntityType: 'UGGo0Ebjl0E',
      filter: `V66IFsw5kq6:eq:${staffId}`,
      fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
      ouMode: 'ACCESSIBLE',
      pageSize: 1
    })
  }
};

// Query to fetch employee objectives using enrollment
const OBJECTIVES_QUERY = {
  objectives: {
    resource: 'events',
    params: ({ programId, stageId, enrollmentId }) => ({
      program: programId,
      programStage: stageId,
      enrollment: enrollmentId,
      ouMode: 'ACCESSIBLE',
      fields: [
        'event',
        'eventDate',
        'dataValues[dataElement,value]'
      ],
      paging: false
    })
  }
};

const ENROLLMENT_MUTATION = {
  resource: 'enrollments',
  type: 'create',
  data: ({ enrollmentData }) => enrollmentData
};

// Query to fetch check-in data
const CHECKIN_QUERY = {
  checkIn: {
    resource: 'events',
    params: ({ programId, stageId, employeeId, period, financialYear }) => ({
      program: programId,
      programStage: stageId,
      filter: [
        `${CHECKIN_IDS.dataElements.period}:eq:${period}`,
        `${CHECKIN_IDS.dataElements.financialYear}:eq:${financialYear}`
      ],
      trackedEntityInstance: employeeId,
      ouMode: 'ACCESSIBLE',
      fields: [
        'event',
        'eventDate',
        'status',
        'trackedEntityInstance',
        'enrollment',
        'orgUnit',
        'dataValues[dataElement,value]'
      ],
      paging: false
    })
  }
};

// Query to get enrollment
const ENROLLMENT_QUERY = {
  enrollment: {
    resource: 'enrollments',
    params: ({ programId, employeeId }) => ({
      program: programId,
      trackedEntity: employeeId,
      ouMode: 'ACCESSIBLE',
      fields: ['enrollment', 'enrollmentDate', 'status'],
      paging: false
    })
  }
};

// Query to fetch objectives by position TEI (fallback)
const OBJECTIVES_BY_POSITION_QUERY = {
  objectives: {
    resource: 'events',
    params: ({ programId, stageId, positionTei }) => ({
      program: programId,
      programStage: stageId,
      ouMode: 'ACCESSIBLE',
      filter: `SlgO60cMK3F:eq:${positionTei}`,
      fields: [
        'event',
        'eventDate',
        'dataValues[dataElement,value]'
      ],
      paging: false
    })
  }
};

const KPIS_FOR_OBJECTIVE_QUERY = {
  kpis: {
    resource: 'events',
    params: ({ programId, stageId, objectiveId }) => ({
      program: programId,
      programStage: stageId, // KPI stage: 'ivyCLjNLFrd'
      filter: `plJovUlxfQ0:eq:${objectiveId}`,
      ouMode: 'ACCESSIBLE',
      fields: [
        'event',
        'dataValues[dataElement,value]'
      ],
      paging: false
    })
  }
};

// Mutation to save check-in
const SAVE_CHECKIN_MUTATION = {
  resource: 'events',
  type: 'create',
  data: ({ eventData }) => eventData
};

// Mutation to update check-in
const UPDATE_CHECKIN_MUTATION = {
  resource: 'events',
  type: 'update',
  id: ({ eventId }) => eventId,
  data: ({ eventData }) => eventData
};

export const useCheckInData = (employeeId, period, financialYear, orgUnit, fiscalYearRange) => {
  // Always call all hooks in the same order
  // First, get the position TEI for the staff member
  const positionQuery = useDataQuery(STAFF_POSITION_QUERY, {
    variables: {
      staffId: employeeId
    },
    skip: !employeeId || !financialYear || !orgUnit
  });

  const positionTei = positionQuery.data?.position?.trackedEntityInstances?.[0]?.trackedEntityInstance;

  // Get enrollment (for the employee)
  const enrollmentQuery = useDataQuery(ENROLLMENT_QUERY, {
    variables: {
      programId: CHECKIN_IDS.programId,
      employeeId
    },
    skip: !employeeId || !financialYear || !orgUnit
  });

  const enrollmentId = enrollmentQuery.data?.enrollment?.enrollments?.[0]?.enrollment;

  // Get objectives using enrollment (primary method)
  const objectivesQuery = useDataQuery(OBJECTIVES_QUERY, {
    variables: {
      programId: CHECKIN_IDS.programId,
      stageId: CHECKIN_IDS.stages.objectives,
      enrollmentId: enrollmentId
    },
    skip: !enrollmentId || !financialYear || !orgUnit
  });

  // Get objectives by position TEI (fallback method)
  const objectivesByPositionQuery = useDataQuery(OBJECTIVES_BY_POSITION_QUERY, {
    variables: {
      programId: CHECKIN_IDS.programId,
      stageId: CHECKIN_IDS.stages.objectives,
      positionTei: positionTei
    },
    skip: !positionTei || !financialYear || !orgUnit
  });

  // Get check-in data
  const checkInQuery = useDataQuery(CHECKIN_QUERY, {
    variables: {
      programId: CHECKIN_IDS.programId,
      stageId: CHECKIN_IDS.stages.checkIn,
      employeeId,
      period,
      financialYear
    },
    skip: !employeeId || !financialYear || !orgUnit
  });

  // Check if financial year is missing
  if (!financialYear) {
    return {
      data: null,
      loading: positionQuery.loading || enrollmentQuery.loading || objectivesQuery.loading || objectivesByPositionQuery.loading || checkInQuery.loading,
      error: new Error('Financial year not available'),
      refetch: () => {
        positionQuery.refetch();
        enrollmentQuery.refetch();
        objectivesQuery.refetch();
        objectivesByPositionQuery.refetch();
        checkInQuery.refetch();
      }
    };
  }

   const processCheckInData = () => {
      // Use objectives from enrollment first, then fallback to position-based
      let objectives = objectivesQuery.data?.objectives?.events || [];

      if (objectives.length === 0) {
        objectives = objectivesByPositionQuery.data?.objectives?.events || [];
      }

      const checkInEvents = checkInQuery.data?.checkIn?.events || [];
      const existingCheckIn =
        checkInEvents.find((e) => e.trackedEntityInstance === employeeId) || null;
      const positionData = positionQuery.data?.position?.trackedEntityInstances?.[0];

      let employeeName = 'Staff';
      if (positionData) {
        const attributes = positionData.attributes || [];
        const firstName = attributes.find(a => a.attribute === 'firstName')?.value || '';
        const surname = attributes.find(a => a.attribute === 'surname')?.value || '';
        employeeName = `${firstName} ${surname}`.trim() || 'Staff';
      }

      // 1. Filter objectives by financial year
      if (fiscalYearRange && fiscalYearRange.start && fiscalYearRange.end && objectives.length > 0) {
        const fyStart = new Date(fiscalYearRange.start);
        const fyEnd = new Date(fiscalYearRange.end);

        objectives = objectives.filter(obj => {
          let eventDate = obj.eventDate;
          let startDate = null;
          let endDate = null;

          if (obj.dataValues) {
            const startDV = obj.dataValues.find(dv => dv.dataElement === 'eDcVy661j9e');
            if (startDV) startDate = startDV.value;
            const endDV = obj.dataValues.find(dv => dv.dataElement === 'J0rjxrDKn1l');
            if (endDV) endDate = endDV.value;
          }

          if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            return start <= fyEnd && end >= fyStart;
          }
          if (startDate) {
            const start = new Date(startDate);
            return start >= fyStart && start <= fyEnd;
          }
          if (eventDate) {
            const event = new Date(eventDate);
            return event >= fyStart && event <= fyEnd;
          }
          return true;
        });
      }

      // 2. FIX DUPLICATES: Deduplicate objectives BEFORE mapping
      const seenKeys = new Set();
      objectives = objectives.filter((obj) => {
        const dv = obj.dataValues || [];
        const get = (id) => dv.find((d) => d.dataElement === id)?.value || '';
        const key = [
          get('EuN5ztFnXsQ'), // objective name
          get('eDcVy661j9e'), // start date
          get('J0rjxrDKn1l'), // end date
          get('yfb9RCRpMB2'), // weight
        ].join('|');
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });

      // 3. Build overall data FIRST (needed for status calculation below)
      const overallData = {};
      if (existingCheckIn) {
        existingCheckIn.dataValues?.forEach(dv => { overallData[dv.dataElement] = dv.value; });
      }

      // ✅ Parse the JSON maps safely
      let empObjData = {};
      let mgrObjData = {};

      const rawEmp = overallData[CHECKIN_IDS.dataElements.employeeComments];
      try {
        if (rawEmp && typeof rawEmp === 'string' && rawEmp.startsWith('{')) {
          empObjData = JSON.parse(rawEmp);
        } else {
          console.warn('⚠️ EMP COMMENTS NOT VALID JSON OR EMPTY:', rawEmp);
        }
      } catch (e) {
        console.error('❌ JSON PARSE FAILED FOR EMP:', e.message);
      }

      const rawMgr = overallData[CHECKIN_IDS.dataElements.managerComments];
      try {
        if (rawMgr && typeof rawMgr === 'string' && rawMgr.startsWith('{')) {
          mgrObjData = JSON.parse(rawMgr);
        }
      } catch (e) {
        console.error('❌ JSON PARSE FAILED FOR MGR:', e.message);
      }

      // ✅ Calculate status BEFORE the empty-objectives early return
      const storedStatus = overallData[CHECKIN_IDS.dataElements.checkInStatus];
      const currentStatus = storedStatus || (() => {
        if (!existingCheckIn) return CHECKIN_STATUS.NOT_STARTED;
        if (overallData[CHECKIN_IDS.dataElements.meetingDate]) return CHECKIN_STATUS.MEETING_SCHEDULED;
        const hasManagerData = !!(
          (mgrObjData['__OVERALL__'] && (mgrObjData['__OVERALL__'].managerComments || mgrObjData['__OVERALL__'].nextSteps)) ||
          overallData[CHECKIN_IDS.dataElements.nextSteps]
        );
        const hasEmployeeData = !!(overallData[CHECKIN_IDS.dataElements.employeeComments] || overallData[CHECKIN_IDS.dataElements.supportNeeds]);
        if (hasManagerData) return CHECKIN_STATUS.REVIEWED;
        if (existingCheckIn.status === 'COMPLETED') return CHECKIN_STATUS.SUBMITTED;
        if (hasEmployeeData) return CHECKIN_STATUS.DRAFT;
        return CHECKIN_STATUS.INITIATED;
      })();

      // ✅ FIX: If no objectives, return empty array but PRESERVE the correct status
      if (objectives.length === 0) {
        return {
          id: existingCheckIn?.event || null,
          trackedEntityInstance: existingCheckIn?.trackedEntityInstance || employeeId,
          enrollment: existingCheckIn?.enrollment || null,
          orgUnit: existingCheckIn?.orgUnit || orgUnit,
          period,
          financialYear,
          date: existingCheckIn?.eventDate || new Date().toISOString().split('T')[0],
          quarter: period === CHECKIN_PERIODS.FIRST ? 1 : 2,
          objectives: [],
          employeeName: employeeName,
          status: currentStatus, // ✅ Use the calculated status, NOT hard-coded NOT_STARTED
          managerReviewComments: (mgrObjData['__OVERALL__'] || {}).managerComments || '',
          managerReviewNextSteps: (mgrObjData['__OVERALL__'] || {}).nextSteps || '',
          meetingDate: overallData[CHECKIN_IDS.dataElements.meetingDate] || '',
          meetingNotes: overallData[CHECKIN_IDS.dataElements.meetingNotes] || '',
          overall: {
            rating: overallData[CHECKIN_IDS.dataElements.overallRating] || '',
            summaryNotes: overallData[CHECKIN_IDS.dataElements.summaryNotes] || '',
            actionItems: overallData[CHECKIN_IDS.dataElements.actionItems] || '',
            achievements: overallData[CHECKIN_IDS.dataElements.keyAchievements] || '',
            challenges: overallData[CHECKIN_IDS.dataElements.challenges] || '',
            recommendations: overallData[CHECKIN_IDS.dataElements.recommendations] || '',
            meetingDate: overallData[CHECKIN_IDS.dataElements.meetingDate] || '',
            meetingNotes: overallData[CHECKIN_IDS.dataElements.meetingNotes] || ''
          }
        };
      }

      // 4. Map objectives with check-in data (this runs exactly once)
      const mappedObjectives = objectives.map((obj, index) => {
        const dataValues = obj.dataValues || [];
        const objectiveData = {};
        dataValues.forEach(dv => { objectiveData[dv.dataElement] = dv.value; });

        // ✅ Resolve the objective ID with the SAME fallback chain used when saving
        const objectiveId =
          objectiveData[CHECKIN_IDS.dataElements.objectiveId] ||
          objectiveData['neuilg3L4d2'] ||
          obj.event ||
          `obj_${index}`;

        // ✅ Find this objective's saved block inside the check-in event
        const checkInData = {};
        if (existingCheckIn) {
          const markerIndex = (existingCheckIn.dataValues || []).findIndex(
            (dv) => dv.dataElement === CHECKIN_IDS.dataElements.objectiveId && dv.value === objectiveId
          );

          if (markerIndex !== -1) {
            const endIdx = Math.min(markerIndex + 15, existingCheckIn.dataValues.length);
            existingCheckIn.dataValues.slice(markerIndex, endIdx).forEach((cdv) => {
              checkInData[cdv.dataElement] = cdv.value;
            });
          }
        }

         return {
          id: objectiveId,
          dhisEventId: obj.event,
          text: objectiveData[CHECKIN_IDS.dataElements.objectiveName] || objectiveData['EuN5ztFnXsQ'] || `Objective ${index + 1}`,
          description: objectiveData['nWqW5BrcgP1'] || '',
          kra: objectiveData[CHECKIN_IDS.dataElements.kra] || '',
          kpi: objectiveData[CHECKIN_IDS.dataElements.kpi] || '',
          tasks: objectiveData[CHECKIN_IDS.dataElements.taskPpn] || '',

          // INJECT ALL FIELDS FROM THE JSON MAPS
          progressStatus: empObjData[objectiveId]?.progressStatus || '',
          percentComplete: parseInt(empObjData[objectiveId]?.percentComplete) || 0,
          employeeComments: empObjData[objectiveId]?.employeeComments || '',
          supportNeeds: empObjData[objectiveId]?.supportNeeds || '',
          supportCategory: empObjData[objectiveId]?.supportCategory || '',
          supportPriority: empObjData[objectiveId]?.supportPriority || '',
          supportNotes: empObjData[objectiveId]?.supportNotes || '',
          achievements: empObjData[objectiveId]?.achievements || '',
          challenges: empObjData[objectiveId]?.challenges || '',

          managerComments: mgrObjData[objectiveId]?.managerComments || '',
          nextSteps: mgrObjData[objectiveId]?.nextSteps || '',
          kpis: [],

          dataValues: obj.dataValues
        };
      });

      // 5. Final return
      return {
        id: existingCheckIn?.event || null,
        trackedEntityInstance: existingCheckIn?.trackedEntityInstance || employeeId,
        enrollment: existingCheckIn?.enrollment || null,
        orgUnit: existingCheckIn?.orgUnit || orgUnit,
        period,
        financialYear,
        date: existingCheckIn?.eventDate || new Date().toISOString().split('T')[0],
        quarter: period === CHECKIN_PERIODS.FIRST ? 1 : 2,

        objectives: mappedObjectives,
        employeeName: employeeName,
        status: currentStatus,
        managerReviewComments: (mgrObjData['__OVERALL__'] || {}).managerComments || '',
        managerReviewNextSteps: (mgrObjData['__OVERALL__'] || {}).nextSteps || '',
        meetingDate: overallData[CHECKIN_IDS.dataElements.meetingDate] || '',
        meetingNotes: overallData[CHECKIN_IDS.dataElements.meetingNotes] || '',
        overall: {
          rating: overallData[CHECKIN_IDS.dataElements.overallRating] || '',
          summaryNotes: overallData[CHECKIN_IDS.dataElements.summaryNotes] || '',
          actionItems: overallData[CHECKIN_IDS.dataElements.actionItems] || '',
          achievements: overallData[CHECKIN_IDS.dataElements.keyAchievements] || '',
          challenges: overallData[CHECKIN_IDS.dataElements.challenges] || '',
          recommendations: overallData[CHECKIN_IDS.dataElements.recommendations] || '',
          meetingDate: overallData[CHECKIN_IDS.dataElements.meetingDate] || '',
          meetingNotes: overallData[CHECKIN_IDS.dataElements.meetingNotes] || ''
        }
      };
    };

  return {
    position: positionQuery.data,
    enrollment: enrollmentQuery.data,
    objectives: objectivesQuery.data,
    objectivesByPosition: objectivesByPositionQuery.data,
    checkIn: checkInQuery.data,
    loading: positionQuery.loading || enrollmentQuery.loading || objectivesQuery.loading || objectivesByPositionQuery.loading || checkInQuery.loading,
    error: positionQuery.error || enrollmentQuery.error || objectivesQuery.error || objectivesByPositionQuery.error || checkInQuery.error,
    data: processCheckInData(),
    refetch: () => {
      positionQuery.refetch();
      enrollmentQuery.refetch();
      objectivesQuery.refetch();
      objectivesByPositionQuery.refetch();
      checkInQuery.refetch();
    }
  };
};

export const useSaveCheckIn = () => {
  const [saveMutation, { loading: saveLoading, error: saveError }] = useDataMutation(SAVE_CHECKIN_MUTATION);

  const saveCheckIn = async (checkInData, status) => {
    const eventData = formatCheckInPayload(checkInData, status);
    
    if (checkInData.id) {
      // ✅ RAW FETCH FOR UPDATE (Exposes the exact 409 reason)
      try {
        console.log('📝 Updating event via raw fetch:', checkInData.id);
        console.log('📦 Payload being sent:', eventData);
        
        const response = await fetch(`../api/32/events/${checkInData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // Passes DHIS2 session cookies
          body: JSON.stringify(eventData)
        });
        
        const result = await response.json();
        console.log('📋 RAW DHIS2 UPDATE RESPONSE FULL:', JSON.stringify(result, null, 2));
        
        if (!response.ok) {
          // Extract the specific DHIS2 conflict message
          const errorMsg = result?.response?.importSummaries?.[0]?.description || 
                           result?.response?.importSummaries?.[0]?.conflicts?.[0]?.value ||
                           result?.message || 
                           'Unknown DHIS2 update error';
          throw new Error(errorMsg);
        }
        
        return result;
      } catch (err) {
        console.error('❌ RAW FETCH UPDATE ERROR:', err.message);
        throw err;
      }
    } else {
      // Fallback to POST for new events
      return await saveMutation({ eventData });
    }
  };

  return { 
    saveCheckIn, 
    loading: saveLoading, 
    error: saveError 
  };
};

export const useInitiateCheckIn = () => {
  const [mutateEvent, { loading: eventLoading, error: eventError }] = useDataMutation(INITIATE_MUTATION);
  const [mutateEnrollment, { loading: enrollmentLoading, error: enrollmentError }] = useDataMutation(ENROLLMENT_MUTATION);

  const initiateCheckIn = async ({ period, financialYear, quarter, trackedEntityInstance, enrollment, orgUnit }) => {
    const today = new Date().toISOString().split('T')[0];
    let currentEnrollment = enrollment;

    console.log('🚀 Initiating check-in with:', { trackedEntityInstance, enrollment, orgUnit, period, financialYear });

    // If no enrollment provided, try to create one
    if (!currentEnrollment && trackedEntityInstance) {
      try {
        console.log('📝 Creating new enrollment...');
        const enrollmentResponse = await mutateEnrollment({
          enrollmentData: {
            trackedEntityInstance,
            program: CHECKIN_IDS.programId,
            orgUnit: orgUnit,
            enrollmentDate: today,
            incidentDate: today,
            status: 'ACTIVE'
          }
        });
        
        console.log('📋 Enrollment response:', enrollmentResponse);
        
        // DHIS2 app-runtime returns the response differently
        // Try multiple paths to find the enrollment ID
        currentEnrollment = 
          enrollmentResponse?.response?.importSummaries?.[0]?.reference ||
          enrollmentResponse?.id ||
          enrollmentResponse?.response?.uid ||
          null;
          
        console.log('✅ Extracted enrollment ID:', currentEnrollment);
        
        if (!currentEnrollment) {
          throw new Error('Could not extract enrollment ID from response');
        }
      } catch (err) {
        console.error('❌ Failed to create enrollment:', err);
        console.error('❌ Full error object:', JSON.stringify(err, null, 2));
        throw new Error(`Failed to enroll staff: ${err.message || 'Unknown error'}`);
      }
    }

    // Now create the check-in event
        // Now create the check-in event using RAW FETCH to expose the exact DHIS2 error
    try {
      console.log('📝 Creating check-in event via raw fetch...');
      
      const payload = {
        program: CHECKIN_IDS.programId,
        programStage: CHECKIN_IDS.stages.checkIn,
        eventDate: today,
        orgUnit: orgUnit,
        trackedEntityInstance: trackedEntityInstance,
        enrollment: currentEnrollment,
        status: 'ACTIVE',
        dataValues: [
          { dataElement: CHECKIN_IDS.dataElements.period, value: period },
          { dataElement: CHECKIN_IDS.dataElements.financialYear, value: financialYear },
          { dataElement: CHECKIN_IDS.dataElements.quarter, value: String(quarter) },
          { dataElement: CHECKIN_IDS.dataElements.date, value: today },
        ],
      };

      // Use raw fetch to bypass app-runtime error masking
      const response = await fetch('../api/32/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Passes DHIS2 session cookies
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      console.log('📋 RAW DHIS2 RESPONSE:', result); // <--- THIS WILL SHOW THE EXACT ERROR

      if (!response.ok) {
        // Extract the specific DHIS2 conflict message
        const errorMsg = result?.response?.importSummaries?.[0]?.description || 
                         result?.response?.importSummaries?.[0]?.conflicts?.[0]?.value ||
                         result?.message || 
                         'Unknown DHIS2 error';
        throw new Error(errorMsg);
      }

      return result;
    } catch (err) {
      console.error('❌ RAW FETCH ERROR MESSAGE:', err.message);
      throw err;
    }
  };

  return { initiateCheckIn, loading: eventLoading || enrollmentLoading, error: eventError || enrollmentError };
};

const INITIATE_MUTATION = { resource: 'events', type: 'create', data: ({ eventData }) => eventData };

const formatCheckInPayload = (checkInData, status) => {
  // 1. Build JSON maps for ALL per-objective fields
  const empObjData = {};
  const mgrObjData = {};

  checkInData.objectives.forEach(obj => {
    empObjData[obj.id] = {
      employeeComments: obj.employeeComments || '',
      supportNeeds: obj.supportNeeds || '',
      supportCategory: obj.supportCategory || '',
      supportPriority: obj.supportPriority || '',
      supportNotes: obj.supportNotes || '',
      progressStatus: obj.progressStatus || '',
      percentComplete: obj.percentComplete || 0,
      achievements: obj.achievements || '',
      challenges: obj.challenges || '',
    };
    
    mgrObjData[obj.id] = {
      managerComments: obj.managerComments || '',
      nextSteps: obj.nextSteps || ''
    };
  });

    mgrObjData['__OVERALL__'] = {
      managerComments: checkInData.managerReviewComments || '',
      nextSteps: checkInData.managerReviewNextSteps || ''
    };

    const dataValues = [
      { dataElement: CHECKIN_IDS.dataElements.period, value: checkInData.period },
      { dataElement: CHECKIN_IDS.dataElements.date, value: checkInData.date },
      { dataElement: CHECKIN_IDS.dataElements.financialYear, value: checkInData.financialYear },
      { dataElement: CHECKIN_IDS.dataElements.quarter, value: checkInData.quarter?.toString() || '1' },
      { dataElement: CHECKIN_IDS.dataElements.checkInStatus, value: status },
      { dataElement: CHECKIN_IDS.dataElements.employeeComments, value: JSON.stringify(empObjData) },
      { dataElement: CHECKIN_IDS.dataElements.managerComments, value: JSON.stringify(mgrObjData) },
      { dataElement: CHECKIN_IDS.dataElements.summaryNotes, value: checkInData.overall?.summaryNotes || '' },
      { dataElement: CHECKIN_IDS.dataElements.actionItems, value: checkInData.overall?.actionItems || '' },
      { dataElement: CHECKIN_IDS.dataElements.keyAchievements, value: '' },
      { dataElement: CHECKIN_IDS.dataElements.challenges, value: '' },
      { dataElement: CHECKIN_IDS.dataElements.recommendations, value: checkInData.overall?.recommendations || '' }
    ];

  // ✅ Only include meeting fields if they actually have values
  if (checkInData.meetingDate) {
    dataValues.push({ dataElement: CHECKIN_IDS.dataElements.meetingDate, value: checkInData.meetingDate });
  }
  if (checkInData.meetingNotes) {
    dataValues.push({ dataElement: CHECKIN_IDS.dataElements.meetingNotes, value: checkInData.meetingNotes });
  }
  // 3. ONLY push objective identifying fields (No duplicate data elements!)
  checkInData.objectives.forEach(obj => {
    dataValues.push(
      { dataElement: CHECKIN_IDS.dataElements.objectiveId, value: obj.id },
      { dataElement: CHECKIN_IDS.dataElements.objectiveName, value: obj.text || '' },
      { dataElement: CHECKIN_IDS.dataElements.kra, value: obj.kra || '' },
      { dataElement: CHECKIN_IDS.dataElements.kpi, value: obj.kpi || '' },
      { dataElement: CHECKIN_IDS.dataElements.taskPpn, value: obj.tasks || '' }
    );
  });

  return {
    event: checkInData.id,
    program: CHECKIN_IDS.programId,
    programStage: CHECKIN_IDS.stages.checkIn,
    eventDate: checkInData.date,
    trackedEntityInstance: checkInData.trackedEntityInstance,
    enrollment: checkInData.enrollment,
    orgUnit: checkInData.orgUnit,
    dataValues,
    status: [CHECKIN_STATUS.SUBMITTED, CHECKIN_STATUS.UNDER_REVIEW, CHECKIN_STATUS.REVIEWED, CHECKIN_STATUS.MEETING_SCHEDULED, CHECKIN_STATUS.COMPLETED].includes(status)
      ? 'COMPLETED' : 'ACTIVE',
  };
};

// Helper function to check if check-in is available
export const getCheckInPeriod = (currentDate, financialYearStart) => {
  const startDate = new Date(financialYearStart);
  const now = new Date(currentDate);
  const monthsDiff = (now.getFullYear() - startDate.getFullYear()) * 12 +
    (now.getMonth() - startDate.getMonth());

  // If you want to base it purely on "how many check-ins elapsed",
  // change these thresholds. Example below assumes mid-year = 1st check-in,
  // end-year = 2nd check-in.
  if (monthsDiff >= 3 && monthsDiff < 9) {
    return CHECKIN_PERIODS.FIRST;
  } else if (monthsDiff >= 9) {
    return CHECKIN_PERIODS.SECOND;
  }
  return null;
};

// Helper to get financial year from dates
export const getFinancialYear = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  return `${start.getFullYear()}-${end.getFullYear()}`;
};

/**
 * Fetch KPI events for a list of objective event IDs.
 * Returns a map: { [objectiveEventId]: kpiObjectArray }
 *
 * Uses the KPI program stage (CHECKIN_IDS.stages.kpi) and links via
 * the plJovUlxfQ0 data element (same link used by ObjectiveList.jsx).
 */
export const fetchKpisForObjectives = async (engine, objectiveIds = []) => {
  if (!engine || !objectiveIds.length) return {};

  const KPI_STAGE = CHECKIN_IDS.stages.kpi; // 'ivyCLjNLFrd'
  const LINK_DE = 'plJovUlxfQ0';
  const PROGRAM_ID = CHECKIN_IDS.programId;

  // Build a filter: plJovUlxfQ0:in:<id1>;<id2>;...
  const filterValue = objectiveIds.join(';');

  try {
    const response = await engine.query({
      kpis: {
        resource: 'events',
        params: {
          program: PROGRAM_ID,
          programStage: KPI_STAGE,
          filter: `${LINK_DE}:in:${filterValue}`,
          ouMode: 'ACCESSIBLE',
          fields: 'event,dataValues[dataElement,value]',
          paging: false,
        },
      },
    });

    const events = response?.kpis?.events || [];
    const map = {};

    events.forEach((ev) => {
      const dvs = {};
      ev.dataValues?.forEach((dv) => {
        dvs[dv.dataElement] = dv.value;
      });

      const parentObjectiveId = dvs[LINK_DE];
      if (!parentObjectiveId) return;

      if (!map[parentObjectiveId]) map[parentObjectiveId] = [];

      const rawWeight = dvs['cb87w04djE6'];
      const parsedWeight = parseFloat(rawWeight);

      map[parentObjectiveId].push({
        id: ev.event,
        name: dvs['DBmylDQA9KX'] || '',
        description: dvs['rQqF75V9t7K'] || '',
        target: dvs['Ru68Ax2Xo2L'] || '',
        weight: Number.isFinite(parsedWeight) ? parsedWeight : '',
      });
    });

    return map;
  } catch (err) {
    console.error('❌ [fetchKpisForObjectives] Failed:', err);
    return {};
  }
};

/**
 * ✅ Determines whether a new check-in can be initiated.
 *
 * Rules:
 *   1. If no previous check-in exists → ALLOW (first check-in of the FY)
 *   2. If the previous check-in is NOT COMPLETED → BLOCK (must close it first)
 *   3. If the previous check-in was COMPLETED less than `gapDays` ago → BLOCK
 *   4. If the same period was already initiated → BLOCK
 *   5. Otherwise → ALLOW
 *
 * @param {object} engine       - DHIS2 data engine (useDataEngine())
 * @param {string} employeeId   - tracked entity instance (TEI) of the staff
 * @param {string} period       - CHECKIN_PERIODS.FIRST or CHECKIN_PERIODS.SECOND
 * @param {string} financialYear - e.g. "2026-2027"
 * @param {number} gapDays      - minimum days between check-ins (default 30)
 * @returns {Promise<{allowed:boolean, reason?:string, nextAllowedDate?:string}>}
 */
export const canInitiateCheckIn = async (engine, employeeId, period, financialYear, gapDays = 30) => {
  if (!engine || !employeeId || !financialYear) {
    return { allowed: false, reason: 'Missing required parameters' };
  }

  try {
    const response = await engine.query({
      checkIns: {
        resource: 'events',
        params: {
          program: CHECKIN_IDS.programId,
          programStage: CHECKIN_IDS.stages.checkIn,
          trackedEntityInstance: employeeId,
          filter: `${CHECKIN_IDS.dataElements.financialYear}:eq:${financialYear}`,
          ouMode: 'ACCESSIBLE',
          fields: 'event,eventDate,dataValues[dataElement,value]',
          paging: false,
        },
      },
    });

    const events = response?.checkIns?.events || [];

    if (events.length === 0) {
      return { allowed: true }; // First check-in
    }

    const sorted = [...events].sort(
      (a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime()
    );
    const latest = sorted[0];

    const dv = {};
    latest.dataValues?.forEach((d) => { dv[d.dataElement] = d.value; });

    const latestStatus = dv[CHECKIN_IDS.dataElements.checkInStatus] || '';
    const latestPeriod = dv[CHECKIN_IDS.dataElements.period] || '';

    // Same period already initiated
    if (latestPeriod === period) {
      return {
        allowed: false,
        reason: `${getCheckInPeriodLabel(period)} has already been initiated for this financial year.`,
      };
    }

    // Previous must be COMPLETED
    if (latestStatus !== CHECKIN_STATUS.COMPLETED) {
      return {
        allowed: false,
        reason: `The previous check-in (${getCheckInPeriodLabel(latestPeriod)}) must be COMPLETED before initiating a new one. Current status: ${latestStatus}.`,
      };
    }

    // Gap check
    const lastCompletedDate = new Date(latest.eventDate);
    const now = new Date();
    const diffDays = Math.floor((now - lastCompletedDate) / (1000 * 60 * 60 * 24));

    if (diffDays < gapDays) {
      const nextAllowed = new Date(lastCompletedDate);
      nextAllowed.setDate(nextAllowed.getDate() + gapDays);
      return {
        allowed: false,
        reason: `The next check-in can only be initiated ${gapDays} days after the previous one was completed. Please wait until ${nextAllowed.toLocaleDateString()}.`,
        nextAllowedDate: nextAllowed.toISOString(),
      };
    }

    return { allowed: true };
  } catch (err) {
    console.error('❌ [canInitiateCheckIn] Error:', err);
    return { allowed: false, reason: 'Failed to verify check-in eligibility. Please try again.' };
  }
};

